import { api } from './api';

export interface ServiceAppointmentDto {
  id: number;
  customerId: number;
  customerName: string;
  customerPhone?: string | null;
  vehicleId?: number | null;
  vehiclePlate: string;
  vehicleInfo?: string | null;
  appointmentDate: string;
  status: number;
  statusText: string;
  notes?: string | null;
  serviceOrderId?: number | null;
  createdAt: string;
}

export interface CalendarEventDto {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor?: string;
  borderColor?: string;
  extendedProps?: {
    type: 'serviceOrder' | 'appointment';
    serviceOrderId?: number;
    appointmentId?: number;
    orderNo?: string;
    plate?: string;
    customerName?: string;
    complaintNotes?: string;
    technicianUserIds?: number[];
    technicianNames?: string;
    responsibleUserName?: string;
    lineDescriptions?: string[];
    status?: number;
    statusText?: string;
    customerPhone?: string;
    vehicleInfo?: string;
    notes?: string;
  };
}

export async function fetchCalendarEvents(start: Date, end: Date): Promise<CalendarEventDto[]> {
  const params = new URLSearchParams({
    start: start.toISOString(),
    end: end.toISOString(),
  });
  const { data } = await api.get<CalendarEventDto[]>(`/calendar/events?${params}`);
  return data;
}

export async function fetchAppointments(params?: {
  customerId?: number;
  fromDate?: string;
  toDate?: string;
  status?: number;
}): Promise<ServiceAppointmentDto[]> {
  const { data } = await api.get<ServiceAppointmentDto[]>('/serviceappointments', { params });
  return data;
}

export async function fetchAppointment(id: number): Promise<ServiceAppointmentDto> {
  const { data } = await api.get<ServiceAppointmentDto>(`/serviceappointments/${id}`);
  return data;
}

export async function createAppointment(appointment: {
  customerId: number;
  vehicleId?: number | null;
  appointmentDate: string;
  notes?: string | null;
}): Promise<{ appointment: ServiceAppointmentDto; smsSent: boolean }> {
  const { data } = await api.post<{ dto: ServiceAppointmentDto; smsSent: boolean }>('/serviceappointments', appointment);
  return { appointment: data.dto, smsSent: data.smsSent };
}

export async function updateAppointment(id: number, appointment: {
  vehicleId?: number | null;
  appointmentDate: string;
  status: number;
  notes?: string | null;
}): Promise<ServiceAppointmentDto> {
  const { data } = await api.put<ServiceAppointmentDto>(`/serviceappointments/${id}`, appointment);
  return data;
}

export async function updateAppointmentStatus(id: number, status: number): Promise<void> {
  await api.put(`/serviceappointments/${id}/status`, { status });
}

export async function convertToServiceOrder(id: number, request?: {
  vehicleId?: number | null;
  responsibleUserId?: number | null;
  complaintNotes?: string | null;
}): Promise<{ serviceOrderId: number }> {
  const { data } = await api.post<{ serviceOrderId: number }>(
    `/serviceappointments/${id}/convert-to-service-order`,
    request || {}
  );
  return data;
}

export async function checkTechnicianConflicts(params: {
  technicianUserId?: number | null;
  start: string;
  end: string;
  excludeServiceOrderId?: number | null;
}): Promise<{ hasConflict: boolean; conflicts: Array<{ type: string; id: number; orderNo?: string; plate?: string; customerName?: string; start: string; end: string }> }> {
  const searchParams = new URLSearchParams();
  if (params.technicianUserId != null) searchParams.set('technicianUserId', String(params.technicianUserId));
  searchParams.set('start', params.start);
  searchParams.set('end', params.end);
  if (params.excludeServiceOrderId != null) searchParams.set('excludeServiceOrderId', String(params.excludeServiceOrderId));
  const { data } = await api.get<{ hasConflict: boolean; conflicts: any[] }>(`/calendar/conflicts?${searchParams}`);
  return data;
}
